import { useState } from "react";
import { toast } from "sonner";
import { Loader2, ArrowRight, Check } from "lucide-react";
import { joinWaitlist } from "@/lib/api";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[a-zA-Z]{2,}$/;

/**
 * variant: "paper" (light insert) | "dark" (graphite panel)
 */
export const WaitlistForm = ({
  variant = "paper",
  source = "site",
  idPrefix = "waitlist",
  onJoined
}) => {
  const [email, setEmail] = useState("");
  const [honey, setHoney] = useState("");
  const [state, setState] = useState("idle"); // idle | loading | done
  const [error, setError] = useState("");
  const [result, setResult] = useState(null);

  const paper = variant === "paper";

  const submit = async (e) => {
    e.preventDefault();
    const value = email.trim();
    if (!value) {
      setError("Enter an address so we know where to send the signal.");
      return;
    }
    if (!EMAIL_RE.test(value)) {
      setError("That address does not resolve. Check it and try again.");
      return;
    }
    setError("");
    setState("loading");
    try {
      const data = await joinWaitlist({ email: value, source, signalKey: honey });
      setResult(data);
      setState("done");
      if (data.status === "duplicate") {
        toast.info("Already registered", { description: data.message });
      } else {
        toast.success("Signal registered", { description: data.message });
      }
      if (onJoined) onJoined(data);
    } catch (err) {
      setState("idle");
      const detail = err?.response?.data?.detail;
      const msg =
        typeof detail === "string"
          ? detail
          : "Transmission failed. Please try again in a moment.";
      setError(msg);
      toast.error("Registration failed", { description: msg });
    }
  };

  if (state === "done") {
    return (
      <div
        className={
          paper
            ? "rounded-[var(--radius)] border border-[hsl(36_20%_74%)] bg-[hsl(38_38%_96%)] p-5"
            : "rounded-[var(--radius)] border border-[hsl(174_72%_42%/0.4)] bg-[hsl(174_40%_40%/0.08)] p-5"
        }
        data-testid={`${idPrefix}-success-message`}
        role="status"
      >
        <div className="flex items-start gap-3">
          <span
            className={
              paper
                ? "mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-[hsl(174_62%_34%)] text-white"
                : "mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-primary text-primary-foreground"
            }
          >
            <Check className="h-3.5 w-3.5" strokeWidth={3} />
          </span>
          <div>
            <p
              className={`font-mono text-[11px] uppercase tracking-[0.16em] ${
                paper ? "text-[hsl(220_12%_40%)]" : "text-muted-foreground"
              }`}
            >
              {result?.status === "duplicate"
                ? "Registry entry exists"
                : "Registry entry created"}
            </p>
            <p
              className={`mt-1 text-sm ${
                paper ? "text-[hsl(var(--ink))]" : "text-foreground"
              }`}
            >
              {result?.message}
            </p>
            <p
              className={`mt-2 text-xs ${
                paper ? "text-[hsl(220_12%_42%)]" : "text-muted-foreground"
              }`}
            >
              {result?.status === "duplicate"
                ? "You will hear from us on each release day and not otherwise. No newsletters, no forwarding."
                : "A confirmation transmission has been sent to your address — check your inbox (and spam, if the channel is filtered). After that you will only hear from us on a release day."}
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <form onSubmit={submit} noValidate className="space-y-3">
      <label
        htmlFor={`${idPrefix}-email`}
        className={`block font-mono text-[11px] uppercase tracking-[0.16em] ${
          paper ? "text-[hsl(220_12%_38%)]" : "text-muted-foreground"
        }`}
      >
        Signal address
      </label>

      <div className="flex flex-col gap-2.5 sm:flex-row">
        <input
          id={`${idPrefix}-email`}
          data-testid={`${idPrefix}-email-input`}
          type="email"
          inputMode="email"
          autoComplete="email"
          placeholder="you@somewhere.real"
          value={email}
          onChange={(e) => {
            setEmail(e.target.value);
            if (error) setError("");
          }}
          aria-invalid={Boolean(error)}
          aria-describedby={error ? `${idPrefix}-error` : undefined}
          className={
            paper
              ? "h-12 w-full flex-1 rounded-[10px] border border-[hsl(36_18%_70%)] bg-white px-4 text-[15px] text-[hsl(var(--ink))] placeholder:text-[hsl(220_8%_58%)] transition-colors duration-150 focus:border-[hsl(174_62%_34%)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(174_62%_34%)]"
              : "h-12 w-full flex-1 rounded-[10px] border border-border bg-[hsl(220_18%_8%)] px-4 text-[15px] text-foreground placeholder:text-[hsl(215_10%_45%)] transition-colors duration-150 focus:border-primary focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          }
        />

        {/* honeypot — hidden from humans */}
        <input
          type="text"
          name="signal_key"
          tabIndex={-1}
          autoComplete="off"
          aria-hidden="true"
          value={honey}
          onChange={(e) => setHoney(e.target.value)}
          className="pointer-events-none absolute h-0 w-0 opacity-0"
          style={{ position: "absolute", left: "-9999px" }}
        />

        <button
          type="submit"
          data-testid={`${idPrefix}-submit-button`}
          disabled={state === "loading"}
          className={
            paper
              ? "group inline-flex h-12 items-center justify-center gap-2 rounded-[10px] bg-[hsl(220_18%_10%)] px-5 font-mono text-[11px] uppercase tracking-[0.18em] text-[hsl(38_38%_94%)] shadow-[0_10px_30px_rgba(0,0,0,0.25)] transition-colors duration-150 hover:bg-[hsl(174_62%_26%)] focus-visible:ring-2 focus-visible:ring-[hsl(174_62%_34%)] disabled:opacity-60"
              : "group inline-flex h-12 items-center justify-center gap-2 rounded-[10px] bg-primary px-5 font-mono text-[11px] uppercase tracking-[0.18em] text-primary-foreground shadow-[0_10px_30px_rgba(0,0,0,0.35)] transition-colors duration-150 hover:bg-[hsl(174_72%_36%)] focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-60"
          }
        >
          {state === "loading" ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin" />
              Transmitting
            </>
          ) : (
            <>
              Join the signal
              <ArrowRight className="h-3.5 w-3.5 transition-transform duration-150 group-hover:translate-x-0.5" />
            </>
          )}
        </button>
      </div>

      {error ? (
        <p
          id={`${idPrefix}-error`}
          data-testid={`${idPrefix}-error-message`}
          role="alert"
          className={`font-mono text-[11px] uppercase tracking-[0.12em] ${
            paper ? "text-[hsl(2_72%_40%)]" : "text-[hsl(2_80%_66%)]"
          }`}
        >
          {error}
        </p>
      ) : (
        <p
          className={`text-xs ${
            paper ? "text-[hsl(220_10%_44%)]" : "text-muted-foreground"
          }`}
        >
          One message on each release day. Nothing else, ever.
        </p>
      )}
    </form>
  );
};
